import React from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { useSite } from "../context/site";
import { Eyebrow, Cta, CtaBand } from "../components/site/kit";
import { Reveal } from "../components/site/motion";
import { useCase, useCases } from "../lib/casesApi";
import { useBlogs } from "../lib/blogsApi";

export default function CaseStudy() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview");
  const { loading, item: data } = useCase(slug, preview);
  const allCases = useCases();
  const allBlogs = useBlogs();
  const { openTalk } = useSite();

  if (loading) return <div className="min-h-screen bg-white" aria-busy="true" />;
  if (!data) return <Navigate to="/404" replace />;

  const more = allCases.filter((c) => c.slug !== slug).slice(0, 3);
  const relatedThinking = allBlogs.filter((b) => b.situation === data.situation).slice(0, 3);
  const opportunity = data.opportunity || [];
  const role = data.role || [];
  const metrics = data.metrics || [];
  const outcomes = data.outcomes || [];

  return (
    <div className="font-body bg-white">
      {preview && (
        <div data-testid="preview-banner" className="sticky top-0 z-50 bg-[#5f8f10] px-6 py-2 text-center font-body text-xs font-semibold uppercase tracking-[0.14em] text-[#161A15]">
          Preview mode{data.published ? "" : " · this case study is not published"}
        </div>
      )}
      {/* Hero */}
      <section className="bg-[#161A15] pb-20 pt-36 text-white md:pt-44">
        <div className="mx-auto max-w-[1360px] px-6 md:px-12 lg:px-16">
          <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/50">
              <li><Link to="/" className="transition-colors hover:text-[#5f8f10]">Home</Link></li>
              <li className="flex items-center gap-2"><span aria-hidden className="text-white/30">/</span><Link to="/our-work" className="transition-colors hover:text-[#5f8f10]">Our Work</Link></li>
              <li className="flex items-center gap-2"><span aria-hidden className="text-white/30">/</span><span className="text-[#5f8f10]">{data.kicker}</span></li>
            </ol>
          </nav>
          <Eyebrow dark>{data.kicker}</Eyebrow>
          <h1 className="font-display mt-6 max-w-[20ch] text-4xl leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            {data.headline}
          </h1>
          <dl className="mt-12 grid gap-px border border-white/12 bg-white/12 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { k: "Client", v: data.client },
              { k: "Engagement", v: data.service },
              { k: "Market", v: data.location },
              { k: "Growth situation", v: data.situation, href: `/our-work?situation=${encodeURIComponent(data.situation)}` },
            ].map((m) => (
              <div key={m.k} className="bg-[#161A15] p-6">
                <dt className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/45">{m.k}</dt>
                {m.href ? (
                  <dd className="mt-2">
                    <Link
                      to={m.href}
                      data-testid="case-situation-tag"
                      aria-label={`See all ${m.v} work`}
                      className="font-body group inline-flex items-center gap-2 border border-[#5f8f10] bg-transparent px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#5f8f10] transition-colors duration-200 hover:bg-[#5f8f10] hover:text-[#161A15]"
                    >
                      {m.v}
                      <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
                    </Link>
                  </dd>
                ) : (
                  <dd className="font-body mt-2 text-base font-normal text-white">{m.v}</dd>
                )}
              </div>
            ))}
          </dl>
          <img src={data.image} alt={`${data.client}: ${data.headline}`} loading="lazy" decoding="async" className="mt-10 aspect-[16/7] w-full object-cover grayscale-0" />
        </div>
      </section>

      {/* The opportunity */}
      <section className="border-b border-black/10 bg-[#F7F6F2]">
        <div className="mx-auto grid max-w-[1360px] gap-10 px-6 py-20 md:grid-cols-12 md:px-12 md:py-28 lg:px-16">
          <div className="md:col-span-4">
            <Eyebrow>The opportunity</Eyebrow>
          </div>
          <div className="md:col-span-8">
            <div className="max-w-2xl space-y-6">
              {opportunity.map((p, i) => (
                <p key={i} className="font-body text-xl font-normal leading-relaxed text-black md:text-2xl">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our role */}
      <section className="border-b border-white/10 bg-[#161A15]">
        <div className="mx-auto grid max-w-[1360px] gap-10 px-6 py-20 md:grid-cols-12 md:px-12 md:py-28 lg:px-16">
          <div className="md:col-span-4">
            <Eyebrow dark>Our role</Eyebrow>
          </div>
          <div className="md:col-span-8">
            <div className="border-t border-white/12">
              {role.map((r, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex items-start gap-4 border-b border-white/12 py-5">
                    <span className="mt-2.5 h-2.5 w-2.5 shrink-0 bg-[#5f8f10]" />
                    <span className="font-body text-lg font-normal leading-relaxed text-white/85">{r}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outcome */}
      <section className="border-t-2 border-[#5f8f10] bg-white">
        <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
          <Eyebrow>Outcome</Eyebrow>
          {metrics.length > 0 && (
            <div className={`mt-10 grid gap-px border border-black/15 bg-black/15 sm:grid-cols-2 ${metrics.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
              {metrics.map((m, i) => (
                <div key={i} className="bg-white p-8 md:p-10">
                  <span className="font-display block text-5xl leading-none text-[#5f8f10] md:text-6xl">{m.metric}</span>
                  <span className="font-body mt-4 block text-base font-normal text-black/70">{m.label}</span>
                </div>
              ))}
            </div>
          )}
          {outcomes.length > 0 && (
            <ul className="mt-8 max-w-2xl space-y-4">
              {outcomes.map((o, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center bg-[#5f8f10] text-sm font-bold text-[#161A15]">✓</span>
                  <span className="font-body text-lg font-normal leading-relaxed text-black">{o}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Related thinking */}
      {relatedThinking.length > 0 && (
        <section className="border-t border-white/10 bg-[#161A15] text-white">
          <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-24 lg:px-16">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Eyebrow dark>Related thinking</Eyebrow>
                <h2 className="font-display mt-4 text-3xl uppercase tracking-tight md:text-4xl">More on <span className="text-[#5f8f10]">{data.situation}</span></h2>
              </div>
              <Link to={`/insights?situation=${encodeURIComponent(data.situation)}`} data-testid="case-related-all" className="font-body inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                All thinking <span aria-hidden>→</span>
              </Link>
            </div>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {relatedThinking.map((b) => (
                <Link key={b.slug} to={`/insights/${b.slug}`} data-testid={`case-related-${b.slug}`} className="group flex h-full flex-col overflow-hidden border border-white/15 bg-[#12150F] transition-colors duration-200 hover:border-[#5f8f10]">
                  <div className="relative overflow-hidden">
                    <img src={b.image} alt="" loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    <span className="font-display absolute left-4 top-3 text-2xl leading-none text-[#5f8f10]">{b.number}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg uppercase leading-[1.05] tracking-tight text-white transition-colors duration-200 group-hover:text-[#5f8f10]">{b.title}</h3>
                    <div className="mt-auto flex items-center justify-between pt-5">
                      <span className="font-body inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">Read <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span></span>
                      <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-white/40">{b.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* More work */}
      <section className="border-t border-black/10 bg-[#F7F6F2]">
        <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-24 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl uppercase tracking-tight text-black md:text-4xl">More work</h2>
            <Link to="/our-work" data-testid="case-back-to-work" className="font-body inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
              All work <span aria-hidden>→</span>
            </Link>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {more.map((c) => (
              <Link key={c.slug} to={`/our-work/${c.slug}`} data-testid={`case-more-${c.slug}`} className="group flex h-full flex-col overflow-hidden border border-black/15 bg-white transition-colors duration-200 hover:border-[#5f8f10]">
                <div className="overflow-hidden">
                  <img src={c.image} alt="" loading="lazy" decoding="async" className="aspect-[16/10] w-full object-cover grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">{c.kicker}</span>
                  <h3 className="font-display mt-2 text-xl uppercase leading-[0.98] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10]">{c.headline}</h3>
                  <span className="font-body mt-auto pt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                    View case <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        heading="Could your business be the next case?"
        onClick={() => openTalk()}
        testId="case-cta"
      />
    </div>
  );
}
